import { DashboardAuthorization, DashboardClaim, UnitLabelType } from "@asd/customer-portal-api";
import { formatShortDate, parseWallClockDate } from "@asd/customer-portal-ui";
import moment, { Moment } from "moment-timezone";

import { getScrambledDate } from "~/providers/ScramblerProvider";
import { ChartDatasetsType, ServiceDateGroups, UsageOverTimeDatasetType } from "./DashboardPPTCharts";

type NormalizedClaim = Omit<DashboardClaim, "serviceDate"> & {
  serviceDate: Moment;
};

export type NormalizedAuthorization = Omit<DashboardAuthorization, "startDate" | "stopDate" | "claims" | "unitLabel"> & {
  startDate: Moment;
  stopDate: Moment;
  forecast: number;
  label: string;
  description: string;
  claims: Array<NormalizedClaim>;
  claimSum: number;
  usedUnits: number;
  authUsagePercentage: number;
  unitUsagePercentage: number;
  serviceDates: Moment[];
  serviceCodes: string[];
  unitLabel?: UnitLabelType | "Dollar";
};

export const getNormalizedAuthorizations = (
  authorizations: DashboardAuthorization[],
  startDateFilter?: Moment,
  endDateFilter?: Moment,
): NormalizedAuthorization[] =>
  authorizations
    .map((authorization: DashboardAuthorization): NormalizedAuthorization => {
      const startDate = parseWallClockDate(authorization.startDate).startOf("day");
      const stopDate = parseWallClockDate(authorization.stopDate).startOf("day");

      const forecast = authorization.allocatedUnits / (stopDate.diff(startDate, "days") + 1); // Total authorized units per day (add 1 to include end date)

      const { claims, claimSum, usedUnits, authUsagePercentage, unitUsagePercentage, serviceDates, serviceCodes } = (
        authorization.claims ?? []
      ).reduce(
        (claimsResult: any, claim) => {
          const serviceDate = parseWallClockDate(claim.serviceDate);

          // The top graph is not affected by the date filter, so we always include all claims in the claimSum and usedUnits calculations
          const claimSum = claimsResult.claimSum + claim.claimDollars;
          const usedUnits = claimsResult.usedUnits + claim.claimUnits;

          let includeClaim = true;
          if (
            startDateFilter?.isValid() &&
            endDateFilter?.isValid() &&
            !serviceDate.isBetween(startDateFilter, endDateFilter, undefined, "[]")
          ) {
            includeClaim = false;
          }

          return {
            claimSum,
            usedUnits,
            authUsagePercentage: (claimSum / authorization.allocatedDollars) * 100,
            unitUsagePercentage: (usedUnits / authorization.allocatedUnits) * 100,
            claims: claimsResult.claims,
            serviceDates: claimsResult.serviceDates,
            serviceCodes: claimsResult.serviceCodes,
            ...(includeClaim && {
              claims: [...claimsResult.claims, { ...claim, serviceDate }],
              serviceDates: [...claimsResult.serviceDates, serviceDate],
              serviceCodes: [...new Set([...claimsResult.serviceCodes, claim.serviceCode])],
            }),
          };
        },
        {
          claims: [],
          claimSum: 0,
          usedUnits: 0,
          authUsagePercentage: 0,
          unitUsagePercentage: 0,
          serviceDates: [] as Moment[],
          serviceCodes: [] as string[],
        },
      );

      return {
        ...authorization,
        startDate,
        stopDate,
        forecast,
        label: formatShortDate(startDate),
        claims,
        claimSum,
        usedUnits,
        authUsagePercentage,
        unitUsagePercentage,
        serviceDates,
        serviceCodes,
        unitLabel: authorization.unitLabel === UnitLabelType.Dollars ? "Dollar" : authorization.unitLabel,
      };
    })
    .sort((a, b) => {
      const aStartDate = a.startDate.valueOf();
      const bStartDate = b.startDate.valueOf();

      return aStartDate < bStartDate ? 1 : aStartDate > bStartDate ? -1 : 0;
    });

export const getChartDatasets = (
  authorization: NormalizedAuthorization,
  startDateFilter: Moment,
  endDateFilter: Moment,
  isScramblerEnabled: boolean,
): ChartDatasetsType => {
  const now = !isScramblerEnabled ? moment() : getScrambledDate(moment());

  const totalUsage = {
    authorizationServiceId: authorization.authorizationServiceId,
    allocatedDollars: authorization.allocatedDollars,
    allocatedUnits: authorization.allocatedUnits,
    adjustedAuthorizedTotal: Math.max(authorization.allocatedDollars - authorization.claimSum, 0),
    adjustedUnitTotal: Math.max(authorization.allocatedUnits - authorization.usedUnits, 0),
    forecast: Math.min(
      Math.round((authorization?.forecast ?? 0) * (now.diff(authorization?.startDate, "days") + 1)),
      authorization?.allocatedUnits ?? 0,
    ),
    claimSum: authorization.claimSum,
    usedUnits: authorization.usedUnits,
    authUsagePercentage: authorization.authUsagePercentage,
    unitUsagePercentage: authorization.unitUsagePercentage,
    label: JSON.stringify({
      text: authorization.label,
      percentage: authorization.unitUsagePercentage,
      description: authorization.description,
    }),
  };

  const dayDiff = endDateFilter.diff(startDateFilter, "days") + 1;
  const startOfDay = moment(startDateFilter);
  startOfDay.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  let groupedBy: "day" | "week" | "month", dateGroups: Moment[] | Moment[][];
  let numberOfGroups;
  let forecast = 0;
  if (dayDiff <= 12) {
    // Day
    groupedBy = "day";
    numberOfGroups = dayDiff;
    dateGroups = Array.from({ length: numberOfGroups }).map((_, index) => moment(startOfDay).add(index, "day"));
    forecast = Math.round(authorization.forecast * 100) / 100;
  } else if (dayDiff <= 84) {
    // Week
    groupedBy = "week";
    numberOfGroups = Math.ceil(dayDiff / 7);
    dateGroups = Array.from({ length: numberOfGroups }).map((_, index) => [
      moment(startOfDay).add(index, "week"),
      moment(startOfDay)
        .add(index + 1, "week")
        .subtract(1, "day"),
    ]);

    const startOfEndDate = moment(endDateFilter);
    startOfEndDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    dateGroups[numberOfGroups - 1][1] = startOfEndDate;
    forecast = Math.round(authorization.forecast * 7 * 100) / 100;
  } else {
    // Month
    groupedBy = "month";
    numberOfGroups = endDateFilter.diff(startDateFilter, "months") + 1;
    dateGroups = Array.from({ length: numberOfGroups }).map((_, index) => moment(startOfDay).add(index, "month").startOf("month"));
    forecast = Math.round(authorization.forecast * 30.44 * 100) / 100; // Average days in a month
  }

  const serviceDateGroups: ServiceDateGroups = {
    groupedBy,
    dateGroups,
  };

  const groupObjects = dateGroups.map((dateGroup) =>
    Array.isArray(dateGroup) ? { dateGroup: [dateGroup[0].valueOf(), dateGroup[1].valueOf()] } : { dateGroup: dateGroup.valueOf() },
  );

  let maxUsedUnits = 0;
  const usageOverTimeDataset = authorization.claims
    .reduce((usageOverTimeResult: Array<UsageOverTimeDatasetType>, claim: NormalizedClaim, index) => {
      if (index === 0) {
        groupObjects.forEach((groupObject) => {
          usageOverTimeResult.push({ ...groupObject });
        });
      }

      if (claim) {
        const { authorizationServiceId, serviceDate, claimUnits } = claim;

        let groupObject = {} as UsageOverTimeDatasetType;
        let groupObjectIndex: number;
        if (serviceDateGroups.groupedBy === "day") {
          const startOfDay = moment(serviceDate);
          startOfDay.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          groupObjectIndex = groupObjects.findIndex(({ dateGroup }) => dateGroup === startOfDay.valueOf());
        } else if (serviceDateGroups.groupedBy === "week") {
          const serviceDateTime = moment(serviceDate).valueOf();
          groupObjectIndex = groupObjects.findIndex(
            ({ dateGroup }) => Array.isArray(dateGroup) && serviceDateTime >= dateGroup[0] && serviceDateTime <= dateGroup[1],
          );
        } else {
          const startOfMonth = moment(serviceDate).startOf("month");
          startOfMonth.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          groupObjectIndex = groupObjects.findIndex(({ dateGroup }) => dateGroup === startOfMonth.valueOf());
        }

        groupObject = groupObjects.at(groupObjectIndex) ?? {};

        groupObject[authorizationServiceId] = Number((+((groupObject[authorizationServiceId] ?? 0) + claimUnits)).toFixed(2));

        maxUsedUnits = Math.max(maxUsedUnits, groupObject[authorizationServiceId]);

        usageOverTimeResult[groupObjectIndex] = groupObject;
      }

      return usageOverTimeResult;
    }, [] as Array<UsageOverTimeDatasetType>)
    .sort((a: any, b: any) => (serviceDateGroups?.groupedBy === "week" ? a.dateGroup[0] - b.dateGroup[0] : a.dateGroup - b.dateGroup));

  const usageOverTimeSeries = {
    id: authorization.authorizationServiceId,
    dataKey: authorization.authorizationServiceId,
    label: authorization.label,
    // highlightScope: {
    //   highlight: "series",
    //   fade: "global",
    // } as HighlightScope,
    valueFormatter: (value: number | null) => (value !== null ? value.toString() : null),
  };

  return {
    totalUsage,
    usageOverTime: {
      serviceDateGroups,
      dataset: usageOverTimeDataset,
      series: usageOverTimeSeries,
      forecast: {
        actual: forecast,
        adjusted: Math.min(forecast, maxUsedUnits),
      },
    },
  };
};
